import ActionTypes from "../constants/ActionTypes";
import BASE_URL from "../constants/Api";
import { authHeader } from "../helpers/auth-header";
import axiosInstance from "../helpers/refreshTokenMiddleware";

/* =========================================================
 * アクセストークン関連処理
 * ========================================================= */
export const accessTokenRequest = () => ({
  type: ActionTypes.ACCESS_TOKEN_REQUEST
});

export const accessTokenSuccess = (otpToken) => {
  // OTPトークンをローカルストレージに保存
  localStorage.setItem("otpToken", otpToken);
  return {
    type: ActionTypes.ACCESS_TOKEN_SUCCESS,
    payload: { otpToken }
  };
};

export const accessTokenFailure = (error) => ({
  type: ActionTypes.ACCESS_TOKEN_FAILURE,
  payload: { message: error }, 
  error: true
});

/**
 * ログインID・パスワードを送信して、OTPトークンを取得する。
 * 正常時：OTPトークンをReduxに保存
 * 異常時：エラーメッセージを表示
 */
export const fetchAccessToken = (credentials) => async (dispatch) => {
  dispatch(accessTokenRequest());

  try {
    const response = await fetch(`${BASE_URL}/users/access-token`, {
      method: "POST",
      headers: authHeader(),
      body: JSON.stringify(credentials),
    });

    const data = await response.json();

    if (!response.ok || data?.status !== 200) {
      const message =
        data.message || `サーバーエラーが発生しました (HTTP ${response.status})`;
      throw new Error(message);
    }

    const otpToken = data?.respons?.otp_token;

    if (otpToken) {
      dispatch(accessTokenSuccess(otpToken));
    } else {
      throw new Error(data.message);
    }
  } catch (error) {
    console.error("fetchAccessToken error:", error);
    dispatch(accessTokenFailure(error.message));
  }
};

/* =========================================================
 * OTP認証関連処理
 * ========================================================= */
export const verifyOtpRequest = () => ({
  type: ActionTypes.VERIFY_OTP_REQUEST,
});

export const verifyOtpSuccess = (accessToken, refreshToken) => {
  // アクセストークンとリフレッシュトークンを保存
  localStorage.setItem("accessToken", accessToken);
  localStorage.setItem("refreshToken", refreshToken);
  return {
    type: ActionTypes.VERIFY_OTP_SUCCESS,
    payload: { accessToken, refreshToken },
  };
};

export const verifyOtpFailure = (error) => ({
  type: ActionTypes.VERIFY_OTP_FAILURE,
  payload: error, 
  error: true,
});

/**
 * OTPコードを送信して、アクセストークンを取得する。
 */
export const verifyOtp = (otpToken, otpCode) => async (dispatch) => {
  dispatch(verifyOtpRequest());

  try {
    const response = await fetch(`${BASE_URL}/users/otp-token`, {
      method: "POST",
      headers: authHeader(),
      body: JSON.stringify({
        otp_token: otpToken,
        otp_code: otpCode,
      }),
    });

    const data = await response.json();

    if (!response.ok || data.status !== 200) {
      dispatch(verifyOtpFailure({
        error_code: data.error_code || data.respons?.error_code || null,
        message: data.message || data.respons?.message || "認証に失敗しました。"
      }));
      return;
    }

    const accessToken = data?.respons?.access_token;
    const refreshToken = data?.respons?.refresh_token;

    dispatch(verifyOtpSuccess(accessToken, refreshToken));
    localStorage.removeItem("lastAction");
  } catch (error) {
    console.error("verifyOtp error:", error);
    dispatch(verifyOtpFailure(error.message));
    throw error;
  }
};

/* =========================================================
 * OTP再送処理
 * ========================================================= */
export const resendOtpRequest = () => ({
  type: ActionTypes.RESEND_OTP_REQUEST
});

export const resendOtpSuccess = (otpToken) => {
  // 新しいOTPトークンをローカルストレージに保存
  localStorage.setItem("otpToken", otpToken);
  return {
    type: ActionTypes.RESEND_OTP_SUCCESS,
    payload: otpToken
  };
};

export const resendOtpFailure = (error) => ({
  type: ActionTypes.RESEND_OTP_FAILURE,
  payload: error
});

export const resendOtp = (otpToken) => async (dispatch) => {
  dispatch(resendOtpRequest());
  try {
    const response = await fetch(BASE_URL + '/users/resend-otp', {
      method: 'POST',
      headers: authHeader(),
      body: JSON.stringify({ otp_token: otpToken  }) 
    });

    const data = await response.json();
    if (!response.ok || data?.status !== 200) {
      const msg =
        data?.respons?.message ||
        data?.message ||
        "認証コードの再送信に失敗しました。";
      throw new Error(msg);
    }

    dispatch(resendOtpSuccess(data.respons.otp_token));
    return true; 
  } catch (error) {
    dispatch(resendOtpFailure(error.message));
    throw error;
  }
};

/* =========================================================
 * ユーザー情報取得処理
 * ========================================================= */
export const fetchUserRequest = () => ({
  type: ActionTypes.FETCH_USER_REQUEST,
});

export const fetchUserSuccess = (user) => ({
  type: ActionTypes.FETCH_USER_SUCCESS,
  payload: user.respons
});

export const fetchUserFailure = (error) => ({
  type: ActionTypes.FETCH_USER_FAILURE,
  payload: error,
  error: true
});

export const fetchUserData = () => async (dispatch) => {
  dispatch(fetchUserRequest());

  try {
    const accessToken = localStorage.getItem('accessToken');

    if (!accessToken) {
      window.location.href = '/login';
      return;
    }

    const response = await axiosInstance.get(BASE_URL + '/users/me', {
      headers: {
        'Authorization': `Bearer ${accessToken}`
      }
    });

    dispatch(fetchUserSuccess(response.data));

  } catch (error) {
    dispatch(fetchUserFailure(error.message));
  }
};

/* =========================================================
 * トークンリフレッシュ処理
 * ========================================================= */
export const refreshAccessTokenRequest = () => ({
  type: ActionTypes.REFRESH_ACCESS_TOKEN_REQUEST
});

export const refreshAccessTokenSuccess = (token) => ({
  type: ActionTypes.REFRESH_ACCESS_TOKEN_SUCCESS,
  payload: token
});

export const refreshAccessTokenFailure = (error) => ({
  type: ActionTypes.REFRESH_ACCESS_TOKEN_FAILURE,
  payload: error,
  error: true
});

export const refreshAccessToken = (refreshToken) => async (dispatch) => {
  dispatch(refreshAccessTokenRequest());
  try {
    const response = await fetch(BASE_URL + '/users/refresh-token', {
      method: 'POST',
      headers: authHeader(),
      body: JSON.stringify({ refresh_token: refreshToken })
    });

    const data = await response.json();
    if (!response.ok) {
      throw new Error(data.message || 'Failed to refresh access token');
    }

    const newAccessToken = data.newAccessToken;
    dispatch(refreshAccessTokenSuccess(newAccessToken));
    return newAccessToken;
  } catch (error) {
    dispatch(refreshAccessTokenFailure(error.message));
    throw error; 
  }
};

/* =========================================================
 * ログアウト処理
 * ========================================================= */
export const logoutRequest = () => ({
  type: ActionTypes.LOGOUT_REQUEST
});

export const logoutSuccess = () => ({
  type: ActionTypes.LOGOUT_SUCCESS
});

export const logoutFailure = (error) => ({
  type: ActionTypes.LOGOUT_FAILURE,
  payload: error,
  error: true
});

export const logoutUser = () => async (dispatch) => {
  dispatch(logoutRequest());

  const remember = localStorage.getItem('remember') === 'true';
  const loginId = localStorage.getItem('loginId');
  const refreshToken = localStorage.getItem("refreshToken");
  const accessToken = localStorage.getItem("accessToken");

  try {
    await axiosInstance.post(
      `${BASE_URL}/users/logout`,
      {
        refresh_token: refreshToken,
        logout_all: false
      },
      {
        headers: {
          "Authorization": `Bearer ${accessToken}`,
        }
      }
    );

    dispatch(logoutSuccess());

  } catch (error) {
    console.warn("Logout API failed:", error?.response?.status, error?.message);
    dispatch(logoutFailure(error.message));
  }

  localStorage.clear();

  // 「ログインIDを記憶する」が有効な場合は再保存
  if (remember) {
    localStorage.setItem('remember', 'true');
    if (loginId) localStorage.setItem('loginId', loginId);
  }

  // 明示的に削除
  localStorage.setItem('lastAction', 'logout');
  window.location.href = "/login";
};